// Store the original fetch function
const originalFetch = window.fetch;

interface FetchLogData {
  url: string;
  method: string;
  timestamp: number;
  duration?: number;
  status?: number;
  error?: string;
  user?: UserInfo | null;
}

interface UserInfo {
  name?: string;
  id?: string;
  email?: string;
}

// Azure Application Insights logger interface
interface AzureLogger {
  trackDependencyData: (data: any) => void;
  trackException: (data: any) => void;
}

// Global user info that can be updated
let currentUserInfo: UserInfo | null = null;

// Global Azure logger instance
let azureLogger: AzureLogger | null = null;

// Whitelist of URL patterns to log (if empty, logs everything)
const urlWhitelist: string[] = ['logic.azure'];

// Function to check if URL should be logged
const shouldLogUrl = (url: string): boolean => {
  if (urlWhitelist.length === 0) return true;
  return urlWhitelist.some(pattern => url.includes(pattern));
};

export const updateUserInfo = (userInfo: UserInfo) => {
  currentUserInfo = userInfo;
};

export const setAzureLogger = (logger: AzureLogger) => {
  azureLogger = logger;
};

export const addToWhitelist = (pattern: string) => {
  if (!urlWhitelist.includes(pattern)) {
    urlWhitelist.push(pattern);
  }
};

export const removeFromWhitelist = (pattern: string) => {
  const index = urlWhitelist.indexOf(pattern);
  if (index > -1) {
    urlWhitelist.splice(index, 1);
  }
};

export const getWhitelist = () => [...urlWhitelist];

export const setupFetchLogging = () => {
  // Override the global fetch function
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [url, options = {}] = args;
    const method = options.method || 'GET';
    const startTime = Date.now();

    const urlString = url.toString();
    const logData: FetchLogData = {
      url: urlString,
      method,
      timestamp: startTime,
      user: currentUserInfo,
    };

    // Only log if URL matches whitelist
    if (shouldLogUrl(urlString)) {
      console.log('🚀 Fetch Request:', {
        url: logData.url,
        method: logData.method,
        headers: options.headers,
        body: options.body,
        user: currentUserInfo
          ? {
              name: currentUserInfo.name,
              id: currentUserInfo.id,
              email: currentUserInfo.email,
            }
          : 'Not authenticated',
      });
    }

    try {
      const response = await originalFetch(...args);
      const duration = Date.now() - startTime;

      logData.duration = duration;
      logData.status = response.status;

      // Only log if URL matches whitelist
      if (shouldLogUrl(urlString)) {
        console.log('✅ Fetch Response:', {
          url: logData.url,
          method: logData.method,
          status: response.status,
          statusText: response.statusText,
          duration: `${duration}ms`,
          ok: response.ok,
          user: currentUserInfo
            ? {
                name: currentUserInfo.name,
                id: currentUserInfo.id,
                email: currentUserInfo.email,
              }
            : 'Not authenticated',
        });

        // Log to Azure Application Insights as dependency (if logger is available)
        if (azureLogger) {
          azureLogger.trackDependencyData({
            id: `${method}_${urlString}_${startTime}`,
            name: `${method} ${urlString}`,
            data: urlString,
            duration: duration,
            success: response.ok,
            responseCode: response.status,
            type: 'Http',
            properties: {
              method: method,
              url: urlString,
              statusText: response.statusText,
              userId: currentUserInfo?.id || 'anonymous',
              userName: currentUserInfo?.name || 'anonymous',
              userEmail: currentUserInfo?.email || 'anonymous',
            },
          });
        }
      }

      return response;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      logData.duration = duration;
      logData.error = errorMessage;

      // Only log if URL matches whitelist
      if (shouldLogUrl(urlString)) {
        console.error('❌ Fetch Error:', {
          url: logData.url,
          method: logData.method,
          error: errorMessage,
          duration: `${duration}ms`,
          user: currentUserInfo
            ? {
                name: currentUserInfo.name,
                id: currentUserInfo.id,
                email: currentUserInfo.email,
              }
            : 'Not authenticated',
        });

        // Log error to Azure Application Insights (if logger is available)
        if (azureLogger) {
          azureLogger.trackException({
            exception: error instanceof Error ? error : new Error(errorMessage),
            properties: {
              type: 'FetchError',
              method: method,
              url: urlString,
              duration: `${duration}ms`,
              userId: currentUserInfo?.id || 'anonymous',
              userName: currentUserInfo?.name || 'anonymous',
              userEmail: currentUserInfo?.email || 'anonymous',
            },
          });

          // Also log as failed dependency
          azureLogger.trackDependencyData({
            id: `${method}_${urlString}_${startTime}_error`,
            name: `${method} ${urlString}`,
            data: urlString,
            duration: duration,
            success: false,
            responseCode: 0,
            type: 'Http',
            properties: {
              method: method,
              url: urlString,
              error: errorMessage,
              userId: currentUserInfo?.id || 'anonymous',
              userName: currentUserInfo?.name || 'anonymous',
              userEmail: currentUserInfo?.email || 'anonymous',
            },
          });
        }
      }

      throw error;
    }
  };
};

export { originalFetch };
